import { NR2J_ACTIVE_PIPELINE_STAGES } from './nr2jConstants.js';

export const NO_MANAGER_FILTER = '__none__';

export const MY_TEAM_STATUT_FILTERS = [
  { id: '', label: 'Tous les statuts' },
  { id: 'ACTIF', label: 'Actif' },
  { id: 'SUSPENDU', label: 'Suspendu' },
  { id: 'EN_ATTENTE', label: 'En attente' },
];

export function memberDisplayName(member) {
  if (!member) return '—';
  const full = `${member.prenom || ''} ${member.nom || ''}`.trim();
  return full || member.email || '—';
}

export function memberInitials(member) {
  const prenom = String(member?.prenom || '').trim();
  const nom = String(member?.nom || '').trim();
  if (prenom && nom) return `${prenom[0]}${nom[0]}`.toUpperCase();
  const base = prenom || nom || String(member?.email || '').trim();
  return base ? base.slice(0, 2).toUpperCase() : '?';
}

export function memberStatutBadge(statut) {
  switch (statut) {
    case 'ACTIF':
      return { label: 'Actif', className: 'bg-emerald-50 text-emerald-800 border-emerald-200' };
    case 'SUSPENDU':
      return { label: 'Suspendu', className: 'bg-rose-50 text-rose-800 border-rose-200' };
    case 'EN_ATTENTE':
      return { label: 'En attente', className: 'bg-amber-50 text-amber-900 border-amber-200' };
    case 'INACTIF':
      return { label: 'Inactif', className: 'bg-gray-100 text-gray-700 border-gray-200' };
    default:
      return { label: statut || '—', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  }
}

/** Libellés page / menu selon le rôle de la session. */
export function myTeamLabels(isSuperAdmin) {
  if (isSuperAdmin) {
    return {
      title: 'Suivi commercial',
      subtitle: 'Pilotage global de l’équipe commerciale NR2J : portefeuilles, validations et priorités.',
      backLabel: 'Suivi commercial',
    };
  }
  return {
    title: 'Mon équipe',
    subtitle: 'Vue manager de vos collaborateurs directs et des priorités du moment.',
    backLabel: 'Mon équipe',
  };
}

export function listManagersFromMembers(members) {
  const byId = new Map();
  for (const member of members || []) {
    if (member.manager?.id && !byId.has(member.manager.id)) {
      byId.set(member.manager.id, { id: member.manager.id, name: memberDisplayName(member.manager) });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

export function filterTeamMembers(members, { managerId = '', statut = '', q = '' } = {}) {
  const needle = String(q || '').trim().toLowerCase();
  return (members || []).filter((member) => {
    if (managerId === NO_MANAGER_FILTER && member.managerUserId) return false;
    if (managerId && managerId !== NO_MANAGER_FILTER && member.managerUserId !== managerId) return false;
    if (statut && member.statut !== statut) return false;
    if (needle) {
      const haystack = `${memberDisplayName(member)} ${member.email || ''}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

function sumStat(members, key) {
  return (members || []).reduce((total, member) => total + (Number(member.stats?.[key]) || 0), 0);
}

export function aggregateTeamKpis(members) {
  return {
    activeMembers: (members || []).filter((member) => member.statut === 'ACTIF').length,
    prospectsActive: sumStat(members, 'prospectsActive'),
    quotesPendingApproval: sumStat(members, 'quotesPendingApproval'),
    tasksOverdue: sumStat(members, 'tasksOverdue'),
    prospectsFollowUp: sumStat(members, 'prospectsFollowUp'),
  };
}

/** Pipeline actif agrégé (hors GAGNE / PERDU), dans l'ordre métier. */
export function aggregateTeamPipeline(members) {
  const totals = Object.fromEntries(NR2J_ACTIVE_PIPELINE_STAGES.map((stage) => [stage, 0]));
  for (const member of members || []) {
    for (const [stage, count] of Object.entries(member.stats?.pipeline || {})) {
      if (stage in totals) totals[stage] += Number(count) || 0;
    }
  }
  return NR2J_ACTIVE_PIPELINE_STAGES.map((stage) => ({ stage, count: totals[stage] }));
}

const APPROVAL_TYPE_LABELS = {
  QUOTE: 'Devis',
  INVOICE: 'Facture',
  CREDIT_NOTE: 'Avoir',
};

/**
 * Alertes prioritaires cockpit : validations en attente puis signaux
 * portefeuille par collaborateur (retards, relances, prospects dormants).
 * `memberName` est toujours renseigné (commercial concerné visible).
 */
export function buildTeamAlerts(members, approvals = [], { limit = 8 } = {}) {
  const alerts = [];
  for (const approval of approvals || []) {
    const doc = approval.document || {};
    const typeLabel = APPROVAL_TYPE_LABELS[approval.entityType] || approval.entityType;
    const who = approval.requestedBy ? memberDisplayName(approval.requestedBy) : null;
    alerts.push({
      id: `approval-${approval.id}`,
      severity: 'high',
      kind: 'APPROVAL',
      title: `${typeLabel} ${doc.number || ''} à valider`.replace(/\s+/g, ' ').trim(),
      detail: doc.organizationName || null,
      memberName: who,
      href: `/nr2j/a-valider?id=${encodeURIComponent(approval.id)}`,
    });
  }
  for (const member of members || []) {
    const stats = member.stats || {};
    const name = memberDisplayName(member);
    const href = `/nr2j/mon-equipe/${member.id}`;
    if (stats.tasksOverdue > 0) {
      alerts.push({
        id: `overdue-${member.id}`,
        severity: 'high',
        kind: 'TASKS_OVERDUE',
        title: `${stats.tasksOverdue} tâche(s) en retard`,
        detail: null,
        memberName: name,
        href,
      });
    }
    if (stats.prospectsFollowUp > 0) {
      alerts.push({
        id: `followup-${member.id}`,
        severity: 'medium',
        kind: 'PROSPECTS_FOLLOW_UP',
        title: `${stats.prospectsFollowUp} prospect(s) à relancer`,
        detail: null,
        memberName: name,
        href,
      });
    }
    if (stats.prospectsStale > 0) {
      alerts.push({
        id: `stale-${member.id}`,
        severity: 'low',
        kind: 'PROSPECTS_STALE',
        title: `${stats.prospectsStale} prospect(s) sans activité récente`,
        detail: null,
        memberName: name,
        href,
      });
    }
  }
  const rank = { high: 0, medium: 1, low: 2 };
  return alerts
    .sort((a, b) => rank[a.severity] - rank[b.severity])
    .slice(0, limit);
}

export function isTaskOverdue(task, now = new Date()) {
  if (!task || task.status !== 'OPEN' || !task.dueAt) return false;
  return new Date(task.dueAt).getTime() < now.getTime();
}
